import { useEffect } from "react";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { RequireAuth } from "@/components/RequireAuth";
import { AppShell } from "@/components/layout/AppShell";
import { TerceroForm } from "@/components/terceros/TerceroForm";
import { useActiveCompany } from "@/lib/company-context";

const searchSchema = z.object({
  /** When provided, the form pre-fills identification + opens the lookup. */
  prefill: z.enum(["NIT", "CC", "CE", "TI", "PP", "PPT"]).optional(),
  number: z.string().optional(),
  /** When "1", the index page wants the form ready for an immediate lookup. */
  openLookup: z.string().optional(),
});

export const Route = createFileRoute("/companies/$companyId/terceros/new")({
  component: NewTerceroPage,
  validateSearch: searchSchema,
});

function NewTerceroPage() {
  return (
    <RequireAuth>
      <AppShell>
        <NewTerceroContent />
      </AppShell>
    </RequireAuth>
  );
}

function NewTerceroContent() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { companyId } = Route.useParams();
  const { prefill, number } = Route.useSearch();
  const { setActiveCompanyId } = useActiveCompany();

  useEffect(() => {
    if (companyId) setActiveCompanyId(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const initial =
    prefill && number
      ? { identificationType: prefill, identificationNumber: number }
      : prefill
        ? { identificationType: prefill }
        : undefined;

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-4">
      <div>
        <Link
          to="/companies/$companyId/terceros"
          params={{ companyId }}
          className="inline-flex w-fit items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3 w-3" />
          {t("terceros.new.back")}
        </Link>
      </div>
      <TerceroForm
        mode={{ kind: "create", companyId }}
        initial={initial}
        onCreated={(created) => {
          void navigate({
            to: "/companies/$companyId/terceros/$terceroId",
            params: { companyId, terceroId: created.id },
          });
        }}
        onOpenExisting={(terceroId) => {
          void navigate({
            to: "/companies/$companyId/terceros/$terceroId",
            params: { companyId, terceroId },
          });
        }}
      />
    </div>
  );
}
